import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleDateTime } from "@/utils/formatUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Button, Col, Divider, Popconfirm, Row, Space, Switch, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteBailAccountMovement, useFilterBailAccountMovements } from "../../../../../common/api/queries";
import { PageSizes, rowGutter, TableSizes } from "../../../../../common/constants";
import type { UUID } from "../../../../../typings/global";
import NotFinishedBatchTag from "../../../batch/components/NotFinishedBatchTag";
import { CommissionsBatchStep } from "../../../batch/enums";
import { CommissionBatchName } from "../../../components/CommissionBatchName";
import { CommissionsCategory } from "../../../enums";
import { BailAccountMovement } from "../../types";
import { BailAccountMovementForm } from "../forms/BailAccountMovementForm";
import { ManualMovementTag } from "../ManualMovementTag";
import { ProcessedManuallyMovementTag } from "../ProcessedManuallyMovementTag";

interface Props {
  agentId: UUID;
  category: CommissionsCategory;
  showFullBatchNameWithLink?: boolean;
  showActions?: boolean;
  showCreateMovementAction?: boolean;
  showFilterSwitch?: boolean;
}

const BailAccountMovementTableView = ({
  agentId,
  category,
  showFullBatchNameWithLink,
  showActions,
  showCreateMovementAction,
  showFilterSwitch,
}: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [bailAccountMovementFormOpen, setBailAccountMovementFormOpen] = useState(false);
  const [bailAccountMovementToUpdate, setBailAccountMovementToUpdate] = useState<BailAccountMovement>();
  const [onlyProcessed, setOnlyProcessed] = useState(false);
  const [onlyManual, setOnlyManual] = useState(false);

  const searchParams = useFilterSearchParams({ pageSize: PageSizes.MEDIUM });

  const { data: movementsPage } = useFilterBailAccountMovements({
    id: agentId,
    object: { ...searchParams, onlyProcessed, onlyManual, category },
  });

  const deleteBailAccountMovement = useDeleteBailAccountMovement();

  const handleBailAccountMovementCreateClick = () => {
    setBailAccountMovementFormOpen(true);
  };

  const handleBailAccountMovementUpdateClick = (movement: BailAccountMovement) => {
    setBailAccountMovementFormOpen(true);
    setBailAccountMovementToUpdate(movement);
  };

  const handleBailAccountMovementFormCancel = () => {
    setBailAccountMovementFormOpen(false);
    setBailAccountMovementToUpdate(undefined);
  };

  const columns: ColumnsType<BailAccountMovement> = [
    {
      key: "commissionsBatch",
      title: t("commissions.bailAccount.attrs.movements.commissionsBatch"),
      width: 330,
      ellipsis: { showTitle: false },
      render: (_, record) =>
        record.commissionsBatch ? (
          <CommissionBatchName
            showLink={showFullBatchNameWithLink}
            batchName={record.commissionsBatch.name}
            batchId={record.commissionsBatch.id}
          />
        ) : (
          <Ellipsis className="sub-header-info dashed">{t("commissions.bailAccount.helpers.noBatch")}</Ellipsis>
        ),
    },
    {
      key: "batchStage",
      width: 170,
      render: (_, record) =>
        record.commissionsBatch ? (
          record.commissionsBatch.step !== CommissionsBatchStep.FINISH && <NotFinishedBatchTag />
        ) : (
          <ProcessedManuallyMovementTag isProcessedManually={record.processedManually} />
        ),
    },
    {
      key: "createdAt",
      title: t("common.createdAt"),
      width: 160,
      render: (_, record) => formatLocaleDateTime(record.createdAt),
    },
    {
      key: "manual",
      width: 140,
      render: (_, record) => <ManualMovementTag isManual={record.manual} note={record.note} />,
    },
    {
      key: "amount",
      title: t("common.amount"),
      align: "right",
      fixed: "right",
      width: 150,
      render: (_, record) => formatLocaleCurrency(record.amount),
    },
  ];

  if (showActions) {
    columns.push({
      key: "actions",
      align: "right",
      fixed: "right",
      width: 200,
      render: (_, record) =>
        showActions &&
        record.manual &&
        !record.commissionsBatch && (
          <>
            <ActionTextIcon
              icon="edit"
              color="blue"
              text={t("common.edit")}
              onClick={() => handleBailAccountMovementUpdateClick?.(record)}
            />

            <Divider orientation="vertical" />

            <Popconfirm
              title={t("commissions.bailAccount.helpers.deleteMovementConfirm")}
              icon={<PopconfirmDeleteIcon />}
              okText={t("common.yes")}
              cancelText={t("common.no")}
              okType="danger"
              onConfirm={() => deleteBailAccountMovement.mutate({ id1: agentId, id2: record.id })}
            >
              <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
            </Popconfirm>
          </>
        ),
    });
  }

  return (
    <>
      <Row gutter={rowGutter} className="margin-bottom-tiny margin-top-small">
        <Col span={12}>
          <Space size="middle" style={{ height: "100%" }} align="end">
            <Space>
              <span>{t("commissions.bailAccount.helpers.filteredMovementsSum")}:</span>
              <b>{formatLocaleCurrency(movementsPage?.amountsSum ?? 0)}</b>
            </Space>

            {showActions && showCreateMovementAction && (
              <Button
                variant="outlined"
                color="primary"
                icon={<AntIcon type="plus-circle" />}
                size="small"
                onClick={handleBailAccountMovementCreateClick}
              >
                {t("commissions.bailAccount.actions.createMovement")}
              </Button>
            )}
          </Space>
        </Col>

        {showFilterSwitch && (
          <Col span={12} className="right-align">
            <div>
              <span className="margin-right-tiny">{t("commissions.bailAccount.helpers.showOnlyProcessed")}</span>
              <Switch
                size="small"
                checkedChildren={<AntIcon type="check" />}
                unCheckedChildren={<AntIcon type="close" />}
                value={onlyProcessed}
                onChange={(value) => {
                  setOnlyProcessed(value);
                  navigate({ search: URLQuery.getTableChangePageIndex(0) }, { replace: true });
                }}
              />
            </div>

            <div>
              <span className="margin-right-tiny">{t("commissions.bailAccount.helpers.showOnlyManual")}</span>
              <Switch
                size="small"
                checkedChildren={<AntIcon type="check" />}
                unCheckedChildren={<AntIcon type="close" />}
                value={onlyManual}
                onChange={(value) => {
                  setOnlyManual(value);
                  navigate({ search: URLQuery.getTableChangePageIndex(0) }, { replace: true });
                }}
              />
            </div>
          </Col>
        )}
      </Row>

      <Row gutter={rowGutter}>
        <Col span={24}>
          <Table<BailAccountMovement>
            {...tableStandardProps()}
            columns={columns}
            scroll={{ x: TableSizes.LARGE }}
            dataSource={movementsPage?.pageData}
            pagination={{
              ...paginationTableProps,
              placement: ["bottomEnd"],
              current: numberOrZero(movementsPage?.pageIndex) + 1,
              pageSize: movementsPage?.pageSize,
              total: movementsPage?.totalElementsCount,
              onChange: (pageIndex) =>
                navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
            }}
          />
        </Col>
      </Row>

      <BailAccountMovementForm
        movement={bailAccountMovementToUpdate}
        agentId={agentId}
        category={category}
        open={bailAccountMovementFormOpen}
        onFormCancel={handleBailAccountMovementFormCancel}
      />
    </>
  );
};

export default BailAccountMovementTableView;
